import { BleClient } from "@capacitor-community/bluetooth-le";
import type { BluetoothLineHandler, BluetoothOptions } from "@/lib/bluetooth";
import {
  BluetoothLineBuffer,
  encodeBluetoothLine,
} from "@/lib/bluetoothLineBuffer";
import {
  DEFAULT_BLE_NOTIFY_UUID,
  DEFAULT_BLE_SERVICE_UUID,
  DEFAULT_BLE_WRITE_UUID,
} from "@/lib/bluetooth";
import { isSupportedNativeParticipantPlatform } from "@/lib/runtime";

let bleInitialized = false;

async function ensureBleInitialized() {
  if (bleInitialized) return;
  await BleClient.initialize({ androidNeverForLocation: true });
  bleInitialized = true;
}

type SimpleBleDevice = {
  id: string;
  name?: string;
};

export class NativeBluetoothClient {
  private deviceId: string | null = null;
  private deviceName: string | null = null;
  private serviceUuid: string = DEFAULT_BLE_SERVICE_UUID;
  private notifyUuid: string = DEFAULT_BLE_NOTIFY_UUID;
  private writeUuid: string = DEFAULT_BLE_WRITE_UUID;
  private onDisconnect?: () => void;
  private lineBuffer: BluetoothLineBuffer;
  private decoder = new TextDecoder();
  private encoder = new TextEncoder();
  private suppressDisconnectEvent = false;

  constructor(onLine: BluetoothLineHandler, onDisconnect?: () => void) {
    this.onDisconnect = onDisconnect;
    this.lineBuffer = new BluetoothLineBuffer(onLine, "NativeBluetooth");
  }

  get isSupported(): boolean {
    return isSupportedNativeParticipantPlatform();
  }

  get connectedDeviceId(): string | null {
    return this.deviceId;
  }

  get connectedDeviceName(): string | null {
    return this.deviceName;
  }

  async listDevices(): Promise<SimpleBleDevice[]> {
    if (!this.isSupported) return [];
    await ensureBleInitialized();

    if (!this.deviceId) return [];
    try {
      const devices = await BleClient.getDevices([this.deviceId]);
      return devices.map((device) => ({
        id: device.deviceId,
        name: device.name,
      }));
    } catch {
      return [];
    }
  }

  async connect(options: BluetoothOptions = {}): Promise<void> {
    if (!this.isSupported) {
      throw new Error("Native BLE is only available in supported native builds.");
    }

    await ensureBleInitialized();

    this.serviceUuid = options.serviceUuid ?? DEFAULT_BLE_SERVICE_UUID;
    this.notifyUuid = options.characteristicUuid ?? DEFAULT_BLE_NOTIFY_UUID;
    this.writeUuid =
      options.writeCharacteristicUuid ?? DEFAULT_BLE_WRITE_UUID;

    let deviceId = options.deviceId ?? null;
    let deviceName: string | null = null;
    const allowDeviceChooserFallback = options.allowDeviceChooserFallback === true;

    if (deviceId) {
      try {
        const known = await BleClient.getDevices([deviceId]);
        const matched = known.find((device) => device.deviceId === deviceId);
        if (matched) {
          deviceName = matched.name ?? null;
        } else if (allowDeviceChooserFallback) {
          deviceId = null;
        }
      } catch {
        if (allowDeviceChooserFallback) {
          deviceId = null;
        }
      }
    }

    if (!deviceId) {
      const requested = await BleClient.requestDevice({
        services: [this.serviceUuid],
        optionalServices: [this.serviceUuid],
        namePrefix: options.deviceNamePrefix,
        displayMode: "list",
      });
      deviceId = requested.deviceId;
      deviceName = requested.name ?? null;
    }

    if (!deviceId) {
      throw new Error("No BLE device selected.");
    }

    await BleClient.connect(deviceId, () => {
      const shouldNotify = !this.suppressDisconnectEvent;
      this.deviceId = null;
      this.deviceName = null;
      this.lineBuffer.reset();
      console.warn("[NativeBluetooth] Device disconnected");
      if (shouldNotify) {
        this.onDisconnect?.();
      }
    });

    this.lineBuffer.reset();
    await BleClient.startNotifications(
      deviceId,
      this.serviceUuid,
      this.notifyUuid,
      (value) => {
        const chunk = this.decoder.decode(
          new Uint8Array(value.buffer, value.byteOffset, value.byteLength)
        );
        this.lineBuffer.pushChunk(chunk);
      }
    );

    this.deviceId = deviceId;
    this.deviceName = deviceName;
  }

  async disconnect(): Promise<void> {
    if (!this.deviceId) return;
    const currentDeviceId = this.deviceId;

    try {
      this.suppressDisconnectEvent = true;
      await BleClient.stopNotifications(
        currentDeviceId,
        this.serviceUuid,
        this.notifyUuid
      );
    } catch (err) {
      console.warn("[NativeBluetooth] stopNotifications error:", err);
    }

    try {
      await BleClient.disconnect(currentDeviceId);
    } catch (err) {
      console.warn("[NativeBluetooth] disconnect error:", err);
    } finally {
      this.deviceId = null;
      this.deviceName = null;
      this.lineBuffer.reset();
      this.suppressDisconnectEvent = false;
    }
  }

  async writeLine(line: string): Promise<void> {
    if (!this.deviceId) {
      throw new Error("No connected BLE device.");
    }
    const payload = encodeBluetoothLine(this.encoder, line);
    const view = new DataView(
      payload.buffer,
      payload.byteOffset,
      payload.byteLength
    );
    await BleClient.write(
      this.deviceId,
      this.serviceUuid,
      this.writeUuid,
      view
    );
  }
}
